import { useState } from "react";
import { Maximize2 } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

// Each TradingView widget is sandboxed in its own iframe so the third-party
// embed script can never race with React's DOM or bubble errors into our app.
export function TVWidget({ src, config, height, interactive = true }) {
  const srcDoc = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>html,body{height:100%;margin:0;padding:0;background:transparent;overflow:hidden;}</style>
  </head>
  <body>
    <div class="tradingview-widget-container" style="height:100%;width:100%;">
      <div class="tradingview-widget-container__widget" style="height:100%;width:100%;"></div>
      <script type="text/javascript" src="${src}" async>${JSON.stringify(config)}</script>
    </div>
  </body>
</html>`;
  return (
    <iframe
      title="tradingview-widget"
      srcDoc={srcDoc}
      scrolling="no"
      loading="lazy"
      style={{ height, width: "100%", border: 0, display: "block", pointerEvents: interactive ? "auto" : "none" }}
    />
  );
}

// ————————————————————————————————————————————————
// Site-wide smooth-scrolling ticker (Bloomberg/CNBC style)
// ————————————————————————————————————————————————
export function TickerTape() {
  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 bg-[#080809]/95 backdrop-blur-md border-t border-white/10"
      style={{ height: 52 }}
      data-testid="market-ticker-bar"
    >
      <TVWidget
        src="https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js"
        height={52}
        config={{
          symbols: [
            { proName: "OANDA:XAUUSD", title: "Gold" },
            { proName: "BITSTAMP:BTCUSD", title: "Bitcoin" },
            { proName: "BITSTAMP:ETHUSD", title: "Ethereum" },
            { proName: "CAPITALCOM:US100", title: "NASDAQ" },
            { proName: "CAPITALCOM:US500", title: "S&P 500" },
            { proName: "CAPITALCOM:US30", title: "Dow Jones" },
            { proName: "FX:EURUSD", title: "EUR/USD" },
            { proName: "TVC:DXY", title: "DXY" },
            { proName: "TVC:USOIL", title: "WTI Oil" },
          ],
          showSymbolLogo: true,
          isTransparent: true,
          displayMode: "adaptive",
          colorTheme: "dark",
          locale: "en",
        }}
      />
    </div>
  );
}

// ————————————————————————————————————————————————
// Bespoke MBM Live Market Dashboard, glass cards + full-chart modal
// ————————————————————————————————————————————————
const MARKETS = [
  { name: "Gold", code: "XAU / USD", symbol: "OANDA:XAUUSD" },
  { name: "Bitcoin", code: "BTC / USD", symbol: "BITSTAMP:BTCUSD" },
  { name: "NASDAQ", code: "US 100", symbol: "CAPITALCOM:US100" },
  { name: "S&P 500", code: "US 500", symbol: "CAPITALCOM:US500" },
  { name: "EUR / USD", code: "FOREX", symbol: "FX:EURUSD" },
  { name: "US Dollar", code: "DXY INDEX", symbol: "CAPITALCOM:DXY" },
  { name: "Crude Oil", code: "WTI", symbol: "TVC:USOIL" },
  { name: "XRP", code: "XRP / USD", symbol: "BITSTAMP:XRPUSD" },
];

function MarketCard({ m, i, onOpen }) {
  return (
    <Reveal delay={0.05 * i}>
      <button
        type="button"
        onClick={() => onOpen(m)}
        className="glass-card group relative w-full text-left overflow-hidden"
        data-testid={`market-card-${i}`}
      >
        <div className="flex items-center justify-between px-4 pt-3.5">
          <div>
            <div className="font-heading text-[15px] font-light text-zinc-100 leading-none">{m.name}</div>
            <div className="section-label text-[7px] text-zinc-600 mt-1.5">{m.code}</div>
          </div>
          <span className="flex items-center gap-1 section-label text-[7px] text-[#7ee0a1]">
            <span className="h-1 w-1 rounded-full" style={{ background: "#7ee0a1", boxShadow: "0 0 6px #7ee0a1" }} />
            LIVE
          </span>
        </div>

        <div className="mt-1 h-[132px] px-1.5">
          <TVWidget
            src="https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js"
            height={132}
            interactive={false}
            config={{
              symbol: m.symbol,
              width: "100%",
              height: "100%",
              locale: "en",
              dateRange: "1D",
              colorTheme: "dark",
              isTransparent: true,
              autosize: true,
              chartOnly: false,
              noTimeScale: true,
              trendLineColor: "rgba(159,211,255,1)",
              underLineColor: "rgba(110,168,255,0.15)",
              underLineBottomColor: "rgba(110,168,255,0)",
              largeChartUrl: "",
            }}
          />
        </div>

        <div className="flex items-center gap-1.5 px-4 pb-3 pt-1 text-[10px] text-zinc-600 group-hover:text-[#9fd3ff] transition-colors duration-300">
          <Maximize2 size={11} strokeWidth={1.5} />
          <span className="section-label text-[8px]">Open full chart</span>
        </div>
      </button>
    </Reveal>
  );
}

export function MarketDashboard() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="markets"
      className="relative py-24 sm:py-32 lg:py-40 border-t border-white/5"
      data-testid="markets-section"
    >
      {/* subtle radial glow backdrop */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.5]"
        style={{ background: "radial-gradient(circle at 80% 0%, rgba(110,168,255,0.06) 0%, rgba(10,10,10,0) 55%)" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-3">
            <SectionLabel>Live Markets</SectionLabel>
            <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-widest text-zinc-500">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#7ee0a1", boxShadow: "0 0 8px #7ee0a1" }} />
              LIVE
            </span>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight max-w-2xl">
            The MBM market desk.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 text-sm text-zinc-500 font-light max-w-xl">
            The markets we watch, live. Tap any instrument for a full interactive chart. For education and analysis only, not financial advice.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {MARKETS.map((m, i) => (
            <MarketCard key={m.symbol} m={m} i={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] h-[85vh] flex flex-col bg-[#0A0A0A] border-white/10 p-0 overflow-hidden gap-0"
          data-testid="market-chart-modal"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <DialogTitle className="font-heading text-xl font-light text-zinc-100">
                {active?.name}
              </DialogTitle>
              <div className="section-label text-[8px] text-zinc-600 mt-1">{active?.code}</div>
            </div>
            <span className="mr-8 flex items-center gap-1.5 section-label text-[8px] text-[#7ee0a1]">
              <span className="h-1 w-1 rounded-full" style={{ background: "#7ee0a1", boxShadow: "0 0 6px #7ee0a1" }} />
              LIVE
            </span>
          </div>
          <div className="flex-1 min-h-0 w-full bg-[#0A0A0A]">
            {active && (
              <TVWidget
                src="https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js"
                height="100%"
                config={{
                  autosize: true,
                  symbol: active.symbol,
                  interval: "60",
                  timezone: "Etc/UTC",
                  theme: "dark",
                  style: "1",
                  locale: "en",
                  hide_side_toolbar: true,
                  allow_symbol_change: false,
                  withdateranges: true,
                  backgroundColor: "rgba(10,10,10,1)",
                  gridColor: "rgba(255,255,255,0.05)",
                }}
              />
            )}
          </div>
          <div className="px-6 py-3 border-t border-white/10 text-[11px] text-zinc-600 font-light">
            Educational reference only. Analysis and decision support, not financial advice.
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
